<template>
  <v-container fluid>
    <v-card outlined elevation="1">
      <v-toolbar color="primary" dark dense flat>
        <v-toolbar-title>{{ title }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn color="white" text small :to="'/completion-reports'">
          <v-icon left>mdi-arrow-left</v-icon> Back
        </v-btn>
      </v-toolbar>

      <v-card-text>
        <v-alert v-if="copiedFrom" type="info" dense text dismissible class="mb-3">
          Copied from {{ copiedFrom }}. Image titles carried over — upload the new photos.
        </v-alert>

        <v-form ref="formRef">
          <v-row dense>

            <!-- Date -->
            <v-col cols="12" md="6">
              <v-text-field outlined dense hide-details type="date" label="Date" v-model="inputs.date"
                :error-messages="errors.date" />
            </v-col>

            <!-- Customer -->
            <v-col cols="12" md="6">
              <v-autocomplete outlined dense hide-details label="Customer" :items="customers" item-value="id"
                :item-text="item => `${item.id} - ${item.name}`" v-model="inputs.customer_id"
                :error-messages="errors.customer_id" />
            </v-col>

            <!-- Project Name -->
            <v-col cols="12" md="6">
              <v-text-field outlined dense hide-details label="Project Name" v-model="inputs.project_name"
                :error-messages="errors.project_name" />
            </v-col>

            <!-- Description -->
            <v-col cols="12">
              <v-textarea outlined dense hide-details label="Description" rows="3" v-model="inputs.description"
                :error-messages="errors.description" />
            </v-col>

            <!-- Scope of Works (LIST) -->
            <v-col cols="12">
              <div class="text-subtitle-2 font-weight-bold mb-1">Scope of Works</div>

              <v-row v-for="(item, i) in inputs.scope_of_works" :key="'scope-' + i" align="center" dense>
                <v-col cols="11">
                  <v-text-field outlined dense hide-details v-model="inputs.scope_of_works[i]"
                    :label="'Scope #' + (i + 1)" />
                </v-col>

                <v-col cols="1">
                  <v-btn icon small color="red" @click="removeScope(i)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small color="blue darken-1" dark class="mt-2" @click="addScope">
                <v-icon left>mdi-plus</v-icon> Add Scope
              </v-btn>

              <div class="red--text text-caption" v-if="errors.scope_of_works">
                {{ errors.scope_of_works[0] }}
              </div>
            </v-col>

            <!-- Work Completed (LIST) -->
            <v-col cols="12">
              <div class="text-subtitle-2 font-weight-bold mb-1">Work Completed</div>

              <v-row v-for="(item, i) in inputs.work_completed" :key="'work-' + i" align="center" dense>
                <v-col cols="11">
                  <v-text-field outlined dense hide-details v-model="inputs.work_completed[i]"
                    :label="'Completed #' + (i + 1)" />
                </v-col>

                <v-col cols="1">
                  <v-btn icon small color="red" @click="removeWork(i)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <v-btn small color="blue darken-1" dark class="mt-2" @click="addWork">
                <v-icon left>mdi-plus</v-icon> Add Work
              </v-btn>

              <div class="red--text text-caption" v-if="errors.work_completed">
                {{ errors.work_completed[0] }}
              </div>
            </v-col>

            <!-- Conclusion -->
            <v-col cols="12">
              <v-textarea outlined dense hide-details label="Conclusion" rows="3" v-model="inputs.conclusion"
                :error-messages="errors.conclusion" />
            </v-col>

            <!-- IMAGES -->
            <v-col cols="12">
              <div class="text-subtitle-2 font-weight-bold mb-2">Images</div>

              <p class="text-caption grey--text text--darken-1 mb-2">
                Up to {{ maxImageMb }} MB per image, {{ maxTotalMb }} MB in total.
              </p>

              <v-alert v-if="uploadError" type="error" dense text class="mb-3">
                {{ uploadError }}
              </v-alert>

              <!-- Rows of images -->
              <v-row dense v-for="(row, rowIndex) in imageRows" :key="'row-' + rowIndex" align="center">
                <v-col v-for="(img, i) in row" :key="'img-' + rowIndex + '-' + i"
                  :sm="Math.floor(spanToNumber(img.span) * 12)" :cols="12">
                  <div class="d-flex flex-column" style="gap:10px">
                    <v-text-field outlined dense hide-details label="Image Name" v-model="img.name" />

                    <v-file-input dense outlined accept="image/*" prepend-inner-icon="mdi-image"
                      append-icon="" label="Image" @change="onFileChange($event, img)"
                      :hide-details="!imageError(img)" :error-messages="imageError(img)"
                      :hint="img.file ? formatBytes(img.file.size) : ''"
                      :persistent-hint="!!img.file" />
                    <v-select dense outlined hide-details label="Span" :items="spanOptions" item-text="label"
                      item-value="value" v-model="img.span" />
                  </div>

                  <v-img v-if="img.preview" :src="img.preview" max-height="200" class="mt-2" contain />

                  <v-btn icon small color="red" @click="removeImage(img)">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </v-col>
              </v-row>

              <!-- ADD IMAGE BUTTON -->
              <v-btn small color="blue darken-1" dark class="mt-2" @click="addImage">
                <v-icon left>mdi-plus</v-icon>
                Add Image
              </v-btn>
            </v-col>
          </v-row>

          <!-- BUTTONS -->
          <v-divider class="my-4" />

          <v-row dense>
            <v-col cols="12" sm="6">
              <v-btn block color="primary" :dark="!saving" :disabled="saving" :loading="saving"
                @click="createReport">
                <v-icon left>mdi-content-save</v-icon> Save
              </v-btn>
            </v-col>

            <v-col cols="12" sm="6">
              <v-btn block color="blue darken-2" dark :to="'/completion-reports'">
                <v-icon left>mdi-list-box</v-icon> Reports
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import appAxios from '@/libs/app-axios'
import { LoadingPopup, SuccessPopup, ErrorPopup, getCurrentDate } from '@/libs/utils'
import {
  MAX_IMAGE_BYTES,
  MAX_TOTAL_UPLOAD_BYTES,
  formatBytes,
  uploadErrorMessage,
} from '@/libs/upload-errors'
import { isAxiosError } from 'axios'

// Text fields the API requires; a "required" error on one we actually filled in
// means the request body was dropped for being too large.
const REQUIRED_TEXT_FIELDS = [
  'project_name',
  'description',
  'date',
  'customer_id',
  'conclusion',
]

export default {
  name: 'CompletionReportsCreate',

  data() {
    return {
      inputs: {
        project_name: '',
        description: '',
        date: getCurrentDate(),
        customer_id: '',
        scope_of_works: [],
        work_completed: [],
        conclusion: '',
        images: [],
      },

      customers: [],
      copiedFrom: '',
      spanOptions: [
        { label: '1/3', value: '1/3' },
        { label: '1/2', value: '1/2' },
        { label: '1', value: '1' }
      ],
      errors: {},
      uploadError: '',
      saving: false,
    }
  },

  computed: {
    title() {
      return this.copiedFrom
        ? `New Completion Report (copy of ${this.copiedFrom})`
        : 'New Completion Report'
    },
    maxImageMb() {
      return MAX_IMAGE_BYTES / (1024 * 1024)
    },
    maxTotalMb() {
      return MAX_TOTAL_UPLOAD_BYTES / (1024 * 1024)
    },
    totalUploadBytes() {
      return this.inputs.images.reduce((sum, img) => sum + (img.file?.size ?? 0), 0)
    },
    imageRows() {
      const rows = []
      let currentRow = []
      let currentSpan = 0

      this.inputs.images.forEach(img => {
        const span = this.spanToNumber(img.span)
        if (currentSpan + span > 1) {
          rows.push(currentRow)
          currentRow = []
          currentSpan = 0
        }
        currentRow.push(img)
        currentSpan += span
      })
      if (currentRow.length) rows.push(currentRow)
      return rows
    }
  },

  mounted() {
    this.fetchCustomers()

    const { duplicateFrom } = this.$route.query
    if (duplicateFrom) this.prefillFromReport(duplicateFrom)
  },

  methods: {
    // Copy an existing report's content into the blank form. The date stays at
    // today's default and no ref_no is carried over — the copy gets its own on
    // save. Images arrive as titles only; the files have to be re-uploaded.
    async prefillFromReport(id) {
      try {
        const { data } = await appAxios().get(`/v3/completion-reports/${id}/duplicate-source`)

        this.inputs.project_name = data.project_name ?? ''
        this.inputs.description = data.description ?? ''
        this.inputs.customer_id = data.customer_id ?? ''
        this.inputs.scope_of_works = data.scope_of_works ?? []
        this.inputs.work_completed = data.work_completed ?? []
        this.inputs.conclusion = data.conclusion ?? ''
        this.inputs.images = (data.images ?? []).map((img) => ({
          name: img.name ?? '',
          span: img.span ?? '1/2',
          file: null,
          preview: null,
        }))

        this.copiedFrom = data.source_ref_no ?? ''
      } catch (e) {
        // Leave the form blank so it still works as a plain create page.
        ErrorPopup.fire('Failed to copy', 'Could not load the report to duplicate.')
      }
    },

    addScope() {
      this.inputs.scope_of_works.push('')
    },
    removeScope(i) {
      this.inputs.scope_of_works.splice(i, 1)
    },

    addWork() {
      this.inputs.work_completed.push('')
    },
    removeWork(i) {
      this.inputs.work_completed.splice(i, 1)
    },

    async fetchCustomers() {
      const res = await appAxios().get('/customers')
      this.customers = res.data
    },
    spanToNumber(span) {
      if (span === '1') return 1
      if (span === '1/2') return 0.5
      if (span === '1/3') return 1 / 3
      return 0.5
    },
    formatBytes,

    // Per-image message: client-side size check first, then whatever the server
    // said about that image's file.
    imageError(img) {
      if (img.file && img.file.size > MAX_IMAGE_BYTES) {
        return `Too large (${formatBytes(img.file.size)}). Maximum ${this.maxImageMb} MB per image.`
      }
      const index = this.inputs.images.indexOf(img)
      return this.errors[`images.${index}.file`] || []
    },

    // Returns a message when the payload can't be sent, '' when it can.
    oversizeMessage() {
      const tooBig = this.inputs.images.filter((img) => img.file && img.file.size > MAX_IMAGE_BYTES)
      if (tooBig.length) {
        return `${tooBig.length} image(s) exceed the ${this.maxImageMb} MB limit. Replace them with smaller files before saving.`
      }
      if (this.totalUploadBytes > MAX_TOTAL_UPLOAD_BYTES) {
        return `The images total ${formatBytes(this.totalUploadBytes)}, over the ${
          this.maxTotalMb
        } MB limit for one report. Remove or shrink some images before saving.`
      }
      return ''
    },

    async createReport() {
      this.errors = {}
      this.uploadError = ''

      const oversize = this.oversizeMessage()
      if (oversize) {
        this.uploadError = oversize
        ErrorPopup.fire('Upload too large', oversize)
        return
      }

      try {
        this.saving = true
        const form = new FormData()
        // Regular inputs
        form.append('project_name', this.inputs.project_name ?? '')
        form.append('description', this.inputs.description ?? '')
        form.append('date', this.inputs.date ?? '')
        form.append('customer_id', this.inputs.customer_id ?? '')
        form.append('conclusion', this.inputs.conclusion ?? '')
        // Scope of works
        this.inputs.scope_of_works.forEach(v => {
          form.append('scope_of_works[]', v)
        })
        // Work completed
        this.inputs.work_completed.forEach(v => {
          form.append('work_completed[]', v)
        })
        // Images
        this.inputs.images.forEach((img, i) => {
          form.append(`images[${i}][name]`, img.name ?? '')
          form.append(`images[${i}][span]`, img.span ?? '1/2')

          if (img.file) {
            form.append(`images[${i}][file]`, img.file)
          }
        })
        LoadingPopup.fire('Saving...')
        await appAxios().post('/v3/completion-reports', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        SuccessPopup.fire('Completion report created!')
        this.$router.push('/completion-reports')
      } catch (err) {
        if (!isAxiosError(err)) {
          ErrorPopup.fire('Failed to create', 'Something went wrong. Please try again.')
          return
        }

        const sentFields = REQUIRED_TEXT_FIELDS.filter((field) => !!this.inputs[field])
        const message = uploadErrorMessage(err, {
          fallback: 'Failed to create the report. Please review the form and try again.',
          uploadedBytes: this.totalUploadBytes,
          sentFields,
        })

        // A dropped body reports every field as missing — showing those inline
        // would flag fields the user actually filled in.
        const bodyDropped = /too large/i.test(message)
        if (err.response?.status === 422 && !bodyDropped) {
          this.errors = err.response.data?.errors ?? {}
        }
        this.uploadError = bodyDropped ? message : ''

        ErrorPopup.fire('Failed to create', message)
      } finally {
        this.saving = false
      }
    },
    addImage() {
      this.inputs.images.push({
        name: '',
        span: '1/2',
        file: null,
        preview: null
      })
    },
    removeImage(img) {
      const index = this.inputs.images.indexOf(img)
      if (index > -1) this.inputs.images.splice(index, 1)
    },
    onFileChange(file, img) {
      img.file = file
      img.preview = file ? URL.createObjectURL(file) : null

      // Drop the server-side error for this slot; it referred to the old file.
      const index = this.inputs.images.indexOf(img)
      this.$delete(this.errors, `images.${index}.file`)
      this.uploadError = this.oversizeMessage()
    },

  },
}
</script>
